/**
 * Brand comparison data for /compare/<brandA>-vs-<brandB> pages.
 *
 * Each entry is hand-curated from public sources (manufacturer press
 * material, NDAA compliance lists, Wayback-archived spec sheets, distributor
 * pricing pages 2024-2026). Values reflect "typical" positioning rather
 * than per-SKU truth — the SKU detail lives in the camera catalog.
 *
 * Goal: SERP-ranking comparison pages with honest verdict-by-use-case
 * narrative, not affiliate spam. Every claim needs a defensible source.
 *
 * Existing bespoke page: /compare/hikvision-vs-dahua (687 lines, kept).
 * Pairs below complement it without duplicating the (hik, dahua) ordering.
 */

export interface BrandFacts {
  /** Display name */
  name: string;
  /** URL slug (lowercase, hyphenated) */
  slug: string;
  /** Country of headquarters */
  hq: string;
  /** Founded year */
  founded: number;
  /** Market tier */
  tier: 'consumer' | 'prosumer' | 'professional' | 'enterprise';
  /** NDAA Section 889 compliant (FAR 52.204-25) */
  ndaa: boolean;
  /** Approximate SKU count we have in catalog (rounded) */
  catalogSize: number;
  /** Flagship AI feature name (marketing term) */
  aiFeature: string;
  /** Flagship low-light feature name */
  lowLightFeature: string;
  /** Typical price range per indoor 4MP camera, EUR */
  typicalPriceEur: [number, number];
  /** Strongest use case (one short phrase) */
  strongestUseCase: string;
  /** Weakest area (honest) */
  weakness: string;
}

export const BRAND_FACTS: Record<string, BrandFacts> = {
  hikvision: {
    name: 'Hikvision',
    slug: 'hikvision',
    hq: 'Hangzhou, China',
    founded: 2001,
    tier: 'professional',
    ndaa: false,
    catalogSize: 19500,
    aiFeature: 'AcuSense',
    lowLightFeature: 'DarkFighter / ColorVu',
    typicalPriceEur: [80, 300],
    strongestUseCase: 'Cost-effective AI at scale',
    weakness: 'NDAA banned — blocked from US federal contracts',
  },
  dahua: {
    name: 'Dahua',
    slug: 'dahua',
    hq: 'Hangzhou, China',
    founded: 2001,
    tier: 'professional',
    ndaa: false,
    catalogSize: 6200,
    aiFeature: 'WizSense / WizMind',
    lowLightFeature: 'Starlight / Smart Dual Light',
    typicalPriceEur: [75, 280],
    strongestUseCase: 'Active deterrence (TiOC) and AI analytics',
    weakness: 'NDAA banned — blocked from US federal contracts',
  },
  axis: {
    name: 'Axis Communications',
    slug: 'axis',
    hq: 'Lund, Sweden',
    founded: 1984,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 1200,
    aiFeature: 'AXIS Object Analytics (ARTPEC chip)',
    lowLightFeature: 'Lightfinder + Forensic WDR',
    typicalPriceEur: [400, 1800],
    strongestUseCase: 'Government, critical infrastructure, integrators',
    weakness: '4-6× the price of Hikvision/Dahua for equivalent specs',
  },
  hanwha: {
    name: 'Hanwha Vision (formerly Samsung)',
    slug: 'hanwha',
    hq: 'Seongnam, South Korea',
    founded: 1977,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 1100,
    aiFeature: 'Wisenet AI (X / P series)',
    lowLightFeature: 'extraLUX',
    typicalPriceEur: [350, 1500],
    strongestUseCase: 'NDAA-compliant alternative to Hikvision at mid-price',
    weakness: 'Smaller global support network than Axis',
  },
  bosch: {
    name: 'Bosch',
    slug: 'bosch',
    hq: 'Gerlingen, Germany',
    founded: 1886,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 800,
    aiFeature: 'Intelligent Video Analytics (IVA) / Camera Trainer',
    lowLightFeature: 'Starlight + Intelligent Auto Exposure',
    typicalPriceEur: [500, 2200],
    strongestUseCase: 'Mission-critical sites (airports, banking, government)',
    weakness: 'Highest price tier; complex configuration',
  },
  uniview: {
    name: 'Uniview',
    slug: 'uniview',
    hq: 'Hangzhou, China',
    founded: 2005,
    tier: 'professional',
    ndaa: false,
    catalogSize: 1800,
    aiFeature: 'LightHunter / EasyAI',
    lowLightFeature: 'LightHunter',
    typicalPriceEur: [90, 260],
    strongestUseCase: 'Budget IP at near-Hikvision specs (~20% cheaper)',
    weakness: 'NDAA banned; smaller installer training network in EU',
  },
  reolink: {
    name: 'Reolink',
    slug: 'reolink',
    hq: 'Shenzhen, China',
    founded: 2009,
    tier: 'consumer',
    ndaa: false,
    catalogSize: 350,
    aiFeature: 'Smart Detection (person/vehicle)',
    lowLightFeature: 'ColorX (full colour night vision)',
    typicalPriceEur: [50, 250],
    strongestUseCase: 'DIY home security, no monthly fees',
    weakness: 'No real installer channel; consumer-only support',
  },
  ezviz: {
    name: 'EZVIZ',
    slug: 'ezviz',
    hq: 'Hangzhou, China',
    founded: 2013,
    tier: 'consumer',
    ndaa: false,
    catalogSize: 300,
    aiFeature: 'EZVIZ Smart Detection',
    lowLightFeature: 'Color Night Vision',
    typicalPriceEur: [40, 180],
    strongestUseCase: 'DIY residential, app-first UX',
    weakness: 'Hikvision subsidiary — same NDAA ban; no pro analytics',
  },
  ubiquiti: {
    name: 'Ubiquiti UniFi Protect',
    slug: 'ubiquiti',
    hq: 'New York, USA',
    founded: 2005,
    tier: 'prosumer',
    ndaa: true,
    catalogSize: 60,
    aiFeature: 'Smart Detections (AI Pro/Plus cameras only)',
    lowLightFeature: '4K with IR + colour modes',
    typicalPriceEur: [200, 500],
    strongestUseCase: 'IT-savvy small business, fully integrated UniFi stack',
    weakness: 'Lock-in to UniFi NVR (Cloud Key / Dream Machine)',
  },
  pelco: {
    name: 'Pelco',
    slug: 'pelco',
    hq: 'Fresno, USA',
    founded: 1957,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 280,
    aiFeature: 'Sarix Pro AI / Smart Compression',
    lowLightFeature: 'SureVision 4.0',
    typicalPriceEur: [600, 2500],
    strongestUseCase: 'US legacy installs, Motorola Solutions ecosystem',
    weakness: 'Slow product cadence vs Axis / Hanwha',
  },
  mobotix: {
    name: 'Mobotix',
    slug: 'mobotix',
    hq: 'Langmeil, Germany',
    founded: 1999,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 200,
    aiFeature: 'MOBOTIX HUB / Cactus Concept',
    lowLightFeature: 'Edge-based image processing',
    typicalPriceEur: [800, 3500],
    strongestUseCase: 'Decentralised architecture (camera = NVR), industrial sites',
    weakness: 'Niche ecosystem; tiny installer network',
  },
  flir: {
    name: 'FLIR (Teledyne)',
    slug: 'flir',
    hq: 'Wilsonville, USA',
    founded: 1978,
    tier: 'enterprise',
    ndaa: true,
    catalogSize: 400,
    aiFeature: 'FLIR Neuro AI (Raven Air / Triton)',
    lowLightFeature: 'Thermal — sees in zero light',
    typicalPriceEur: [1500, 8000],
    strongestUseCase: 'Perimeter intrusion detection, dual-spectrum (visible+thermal)',
    weakness: 'Premium pricing; visible-only models limited',
  },
  hilook: {
    name: 'HiLook',
    slug: 'hilook',
    hq: 'Hangzhou, China',
    founded: 2017,
    tier: 'prosumer',
    ndaa: false,
    catalogSize: 800,
    aiFeature: 'Motion Detection 2.0',
    lowLightFeature: 'Limited Smart Hybrid Light on select SKUs',
    typicalPriceEur: [50, 180],
    strongestUseCase: 'Entry-level installer projects, Hikvision sister brand',
    weakness: 'No AI analytics; same NDAA ban as parent Hikvision',
  },
  amcrest: {
    name: 'Amcrest',
    slug: 'amcrest',
    hq: 'Houston, USA',
    founded: 2014,
    tier: 'consumer',
    ndaa: false,
    catalogSize: 200,
    aiFeature: 'AI Smart Detection',
    lowLightFeature: 'Full Color Night Vision',
    typicalPriceEur: [60, 220],
    strongestUseCase: 'DIY US market, Dahua OEM rebrand',
    weakness: 'Same NDAA ban as Dahua parent; consumer-only channel',
  },
};

export interface BrandComparison {
  /** URL slug, alphabetical: e.g. axis-vs-hanwha (sorted client-side) */
  slug: string;
  /** First brand key (left side of comparison) */
  brandA: keyof typeof BRAND_FACTS;
  /** Second brand key */
  brandB: keyof typeof BRAND_FACTS;
  /** Curated "use case verdict" — which wins for what scenario */
  verdicts: {
    homeOwner: { winner: keyof typeof BRAND_FACTS | 'tie'; reason: string };
    smb: { winner: keyof typeof BRAND_FACTS | 'tie'; reason: string };
    enterprise: { winner: keyof typeof BRAND_FACTS | 'tie'; reason: string };
  };
  /** Pricing narrative — one sentence on the price relationship */
  pricingNarrative: string;
  /** Optional priority for sitemap (higher = more important) */
  priority?: number;
}

/**
 * 12 priority brand pairs (excluding hikvision-vs-dahua which has its
 * own bespoke 687-line page). Selected by global search-volume estimate
 * from SEMrush/Ahrefs proxy data + niche pairs we can dominate.
 */
export const BRAND_COMPARISONS: BrandComparison[] = [
  // High-volume head queries
  {
    slug: 'hikvision-vs-axis',
    brandA: 'hikvision',
    brandB: 'axis',
    verdicts: {
      homeOwner: { winner: 'hikvision', reason: '4-6× cheaper for equivalent home-grade specs; Axis overkill for residential.' },
      smb: { winner: 'hikvision', reason: 'AcuSense matches AXIS Object Analytics at 1/4 the cost; SMB rarely needs Axis-grade reliability.' },
      enterprise: { winner: 'axis', reason: 'NDAA compliance, 10-year firmware support, government certifications — Hikvision banned from US federal.' },
    },
    pricingNarrative: 'Hikvision typical 4MP camera €80-300 vs Axis €400-1800 — roughly 4-6× price premium for Axis at the equivalent megapixel/feature tier.',
    priority: 0.9,
  },
  {
    slug: 'dahua-vs-uniview',
    brandA: 'dahua',
    brandB: 'uniview',
    verdicts: {
      homeOwner: { winner: 'uniview', reason: '~20% cheaper than Dahua for similar specs; both are Chinese, both NDAA-banned — pick on price.' },
      smb: { winner: 'dahua', reason: 'WizSense AI more mature than EasyAI; better TiOC active deterrence line.' },
      enterprise: { winner: 'tie', reason: 'Both NDAA banned — neither fits enterprise/government. Pick non-Chinese brand instead.' },
    },
    pricingNarrative: 'Uniview undercuts Dahua by ~20% across the catalog (4MP bullet: Uniview ~€90 vs Dahua ~€110). Both Chinese, both NDAA-banned.',
    priority: 0.8,
  },
  {
    slug: 'axis-vs-hanwha',
    brandA: 'axis',
    brandB: 'hanwha',
    verdicts: {
      homeOwner: { winner: 'tie', reason: 'Both are overkill for home; either choice fine if budget allows.' },
      smb: { winner: 'hanwha', reason: '30-40% cheaper than Axis with comparable Wisenet AI; better value for SMB integrator projects.' },
      enterprise: { winner: 'axis', reason: 'Larger global support network, longer firmware lifecycle, broader analytics ecosystem.' },
    },
    pricingNarrative: 'Hanwha Wisenet positioned 30-40% below Axis for equivalent feature class. Both NDAA-compliant.',
    priority: 0.9,
  },
  {
    slug: 'hikvision-vs-hanwha',
    brandA: 'hikvision',
    brandB: 'hanwha',
    verdicts: {
      homeOwner: { winner: 'hikvision', reason: 'Hanwha overkill for residential; Hikvision DS-2CD line covers home use at 1/3 the price.' },
      smb: { winner: 'hanwha', reason: 'NDAA-compliant means no US federal blocker; AI parity at 50% Hikvision-Axis price gap.' },
      enterprise: { winner: 'hanwha', reason: 'NDAA compliance is non-negotiable in US federal/critical-infrastructure; Hikvision auto-disqualified.' },
    },
    pricingNarrative: 'Hanwha ~3× Hikvision price for similar specs, but NDAA-compliant. Sweet spot for SMB that needs NDAA without paying Axis tax.',
    priority: 0.8,
  },
  {
    slug: 'hikvision-vs-uniview',
    brandA: 'hikvision',
    brandB: 'uniview',
    verdicts: {
      homeOwner: { winner: 'uniview', reason: '~20% cheaper for similar 4MP/8MP cameras; both NDAA-banned so same compliance status.' },
      smb: { winner: 'hikvision', reason: 'AcuSense AI more mature than LightHunter AI; broader installer network in EU.' },
      enterprise: { winner: 'tie', reason: 'Both NDAA banned — both blocked from US federal contracts.' },
    },
    pricingNarrative: 'Uniview undercuts Hikvision by ~20% on equivalent feature class. AI maturity favours Hikvision; pricing favours Uniview.',
    priority: 0.8,
  },
  {
    slug: 'axis-vs-bosch',
    brandA: 'axis',
    brandB: 'bosch',
    verdicts: {
      homeOwner: { winner: 'tie', reason: 'Both heavily over-specced for home; pick whichever your integrator already supports.' },
      smb: { winner: 'axis', reason: 'Better integrator ecosystem and broader product range for typical SMB needs.' },
      enterprise: { winner: 'bosch', reason: 'Mission-critical sites (banks, airports, energy) lean Bosch for paranoia-grade reliability + IVA analytics.' },
    },
    pricingNarrative: 'Bosch ~10-25% premium over Axis at equivalent feature class. Both NDAA-compliant, both top-tier reliability.',
    priority: 0.7,
  },
  // Consumer-pro crossover (DIY vs professional)
  {
    slug: 'hikvision-vs-reolink',
    brandA: 'hikvision',
    brandB: 'reolink',
    verdicts: {
      homeOwner: { winner: 'reolink', reason: 'Reolink built for DIY: app-first, no NVR config, cloud-optional, ~€100/cam. Hikvision needs an installer.' },
      smb: { winner: 'hikvision', reason: 'Real NVR + ONVIF + VMS integration. Reolink hits a ceiling above ~8 cameras.' },
      enterprise: { winner: 'hikvision', reason: 'Reolink lacks the AI analytics, enterprise VMS, and SLA that any commercial deployment needs.' },
    },
    pricingNarrative: 'Reolink ~€50-250 per cam (DIY tier). Hikvision ~€80-300 per cam (pro-grade hardware). Reolink wins on UX, Hikvision on growth headroom.',
    priority: 0.8,
  },
  {
    slug: 'dahua-vs-ezviz',
    brandA: 'dahua',
    brandB: 'ezviz',
    verdicts: {
      homeOwner: { winner: 'ezviz', reason: 'EZVIZ app-first DIY experience matches what most homeowners actually want.' },
      smb: { winner: 'dahua', reason: 'WizSense AI + real ONVIF NVR support. EZVIZ hits ceiling above ~4 cameras.' },
      enterprise: { winner: 'dahua', reason: 'EZVIZ is consumer-only — no enterprise VMS or analytics path.' },
    },
    pricingNarrative: 'EZVIZ €40-180 per cam (consumer app-first). Dahua €75-280 per cam (pro grade). EZVIZ wins DIY, Dahua wins anything scaling beyond a house.',
    priority: 0.7,
  },
  {
    slug: 'ubiquiti-vs-reolink',
    brandA: 'ubiquiti',
    brandB: 'reolink',
    verdicts: {
      homeOwner: { winner: 'reolink', reason: 'Reolink works without buying the whole UniFi stack; better DIY value.' },
      smb: { winner: 'ubiquiti', reason: 'UniFi Protect scales to dozens of cameras + integrates with VLAN/firewall on the same controller.' },
      enterprise: { winner: 'ubiquiti', reason: 'NDAA-compliant + integrated network management beats Reolink at any commercial scale.' },
    },
    pricingNarrative: 'Reolink standalone ~€100/cam. Ubiquiti needs UniFi Cloud Key (~€200) + cameras ~€200-500 — higher upfront, much better at scale.',
    priority: 0.6,
  },
  // German / European focus
  {
    slug: 'bosch-vs-mobotix',
    brandA: 'bosch',
    brandB: 'mobotix',
    verdicts: {
      homeOwner: { winner: 'tie', reason: 'Both overkill for home; pick the brand your integrator certifies on.' },
      smb: { winner: 'bosch', reason: 'Broader product range, easier to source parts, more installer training in EU.' },
      enterprise: { winner: 'mobotix', reason: 'Decentralised architecture (each camera is also the NVR) appeals to industrial sites where central NVR failure is unacceptable.' },
    },
    pricingNarrative: 'Both premium German engineering. Mobotix sometimes 20-40% above Bosch due to integrated edge processing — niche use case justifies the premium.',
    priority: 0.5,
  },
  // Thermal / specialty
  {
    slug: 'flir-vs-axis',
    brandA: 'flir',
    brandB: 'axis',
    verdicts: {
      homeOwner: { winner: 'axis', reason: 'Thermal is overkill and overpriced for residential — visible Axis solves 99% of cases.' },
      smb: { winner: 'axis', reason: 'Cheaper visible cameras handle SMB scenarios. FLIR only justified for specific dark-sky perimeter use cases.' },
      enterprise: { winner: 'flir', reason: 'Perimeter intrusion in zero-light, weather-immune detection, dual-spectrum analytics — FLIR territory.' },
    },
    pricingNarrative: 'FLIR €1500-8000 per thermal/dual-spec cam vs Axis €400-1800 per visible cam — FLIR is 3-5× more, only worth it for thermal-specific use cases.',
    priority: 0.5,
  },
  // Sister-brand comparisons (high-intent niche)
  {
    slug: 'hikvision-vs-hilook',
    brandA: 'hikvision',
    brandB: 'hilook',
    verdicts: {
      homeOwner: { winner: 'hilook', reason: 'Same parent company, 30-40% cheaper, identical NDAA status. Home use rarely needs AcuSense.' },
      smb: { winner: 'hikvision', reason: 'AcuSense AI matters for SMB; HiLook lacks it. Worth the price step-up.' },
      enterprise: { winner: 'tie', reason: 'Both NDAA banned — both disqualified from US federal. Pick non-Chinese brand instead.' },
    },
    pricingNarrative: 'HiLook is Hikvision\'s entry-level sub-brand: ~€50-180 vs Hikvision ~€80-300. Identical NDAA status (both banned); HiLook drops the AI features.',
    priority: 0.6,
  },
];
